import React from "react";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Alert, AlertDescription } from "../../components/ui/Alert";

const Print = () => {
  const { id } = useParams();

  const { data, isLoading, error } = useQuery({
    queryKey: ["finalPrintInformation"],
    queryFn: async () => {
      const req = await axiosget(`ms_application_fee/receipt/${id}`);
      return req?.data;
    },
  });

  return (
    <div className="min-h-screen bg-neutral-100 py-6 print:bg-white print:py-0">
      <style>
        {`
          @media print {
            @page {
              margin: 8mm;
              size: A4;
            }
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
      {isLoading ? (
        <Loader />
      ) : (
        <div className="container mx-auto px-4 print:px-0 print:max-w-full">
          {error ? (
            <Alert variant="danger"><AlertDescription>{error}</AlertDescription></Alert>
          ) : (
            <div className="max-w-3xl mx-auto bg-white shadow-xl rounded-xl overflow-hidden print:shadow-none print:rounded-none print:max-w-full">
              <div className="p-6 print:p-4" id="printDocument">
                {/* Header with Centered Logo on Top */}
                <div className="text-center mb-4 pb-4 border-b-2 border-primary-600 print:mb-3 print:pb-3">
                  <img
                    src="/images/college-logo.png"
                    alt="Logo"
                    className="w-16 h-16 object-contain mx-auto mb-2 print:w-14 print:h-14 print:mb-1"
                  />
                  <h1 className="text-lg font-bold text-neutral-900 leading-tight print:text-base">
                    ADAMU TAFAWA BALEWA COLLEGE OF EDUCATION, KANGERE
                  </h1>
                  <p className="text-sm font-medium text-neutral-600 print:text-xs">BAUCHI STATE, NIGERIA</p>
                  <span className="inline-block mt-2 px-4 py-1 bg-primary-600 text-white text-xs font-semibold rounded-full print:mt-1 print:px-3 print:py-0.5 print:text-[10px]">
                    ADMISSION APPLICATION SLIP
                  </span>
                </div>

                {/* Two Column Layout: Photo + Details */}
                <div className="flex gap-6 mb-4 print:gap-4 print:mb-3">
                  {/* Left: Photo */}
                  <div className="flex-shrink-0">
                    <div className="w-32 h-40 rounded-lg overflow-hidden border-2 border-neutral-300 shadow print:w-24 print:h-32 print:border">
                      <img
                        src={data?.bio?.photo?.link}
                        alt="Passport"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <p className="text-center text-[10px] text-neutral-500 mt-1 font-medium print:text-[8px]">
                      Applicant Photo
                    </p>
                  </div>

                  {/* Right: Key Details */}
                  <div className="flex-1 space-y-2 print:space-y-1">
                    {/* Application ID - Highlighted */}
                    <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-lg p-3 print:p-2">
                      <p className="text-[10px] text-primary-600 font-medium mb-0.5 print:text-[8px]">Application ID</p>
                      <p className="text-lg font-bold text-primary-700 font-mono print:text-sm">
                        {data?.find?.application?.code}
                      </p>
                    </div>

                    {/* Name */}
                    <div className="bg-neutral-50 rounded-lg p-2.5 print:p-1.5">
                      <p className="text-[10px] text-neutral-500 font-medium print:text-[8px]">Full Name</p>
                      <p className="text-sm font-bold text-neutral-900 uppercase print:text-xs">
                        {data?.bio?.firstname} {data?.bio?.middlename} {data?.bio?.lastname}
                      </p>
                    </div>

                    {/* Programme */}
                    <div className="bg-neutral-50 rounded-lg p-2.5 print:p-1.5">
                      <p className="text-[10px] text-neutral-500 font-medium print:text-[8px]">Programme Applied</p>
                      <p className="text-sm font-semibold text-neutral-900 print:text-xs">
                        <span className="text-primary-600">{data?.find?.programme?.code}</span>
                        <span className="mx-1.5 text-neutral-400">—</span>
                        {data?.find?.programme?.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Compact Info Grid */}
                <div className="grid grid-cols-2 gap-2 mb-4 print:gap-1.5 print:mb-3">
                  <InfoRow label="Academic Session" value={`${data?.find?.application?.session?.code} — ${data?.find?.application?.session?.name}`} />
                  <InfoRow label="Date of Birth" value={new Date(data?.bio?.dateofbirth).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} />
                  <InfoRow label="Gender" value={data?.bio?.gender} />
                  <InfoRow label="Phone Number" value={data?.bio?.phonenumber} />
                  <InfoRow label="Email" value={data?.bio?.emailaddress} className="col-span-2" />
                  <InfoRow
                    label={data?.find?.trxdata?.gateway ? "Payment Reference" : "Payment Reference (RRR)"}
                    value={data?.find?.trxcode}
                    highlight
                    className="col-span-2"
                  />
                </div>

                {/* Footer */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center print:p-2">
                  <p className="text-[10px] text-amber-800 print:text-[9px]">
                    <span className="font-semibold">Important:</span> Keep this slip safe and present it during admission screening.
                    This serves as proof of application and payment.
                  </p>
                </div>

                {/* Generated Date */}
                <p className="text-center text-[10px] text-neutral-400 mt-3 print:mt-2 print:text-[8px]">
                  Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>

                {/* Print Button */}
                <div className="mt-4 text-center print:hidden">
                  <button
                    onClick={() => window.print()}
                    className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-all font-semibold shadow-md hover:shadow-lg text-sm"
                  >
                    🖨️ Print Application Slip
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Compact info row component
const InfoRow = ({ label, value, highlight, className = "" }) => (
  <div className={`${highlight ? 'bg-primary-50 border border-primary-200' : 'bg-neutral-50'} rounded-lg p-2 print:p-1.5 ${className}`}>
    <p className="text-[10px] text-neutral-500 font-medium print:text-[8px]">{label}</p>
    <p className={`text-xs font-semibold ${highlight ? 'text-primary-700' : 'text-neutral-800'} break-all print:text-[10px]`}>
      {value}
    </p>
  </div>
);

export default Print;
